import fmt_date, { format_datetime_dd_mmm_yy as fmt_datetime } from '../../../utils/format_date_dd_mmm_yy';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Badge, Button, Card, CardBody, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from 'reactstrap';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';
import EvidenceModal from './EvidenceModal';
import TaskCreateModal from './TaskCreateModal';
import CorpusSources from './CorpusSources';
import ResolverMergeCard, { count_undecided_merges, is_resolver_merge, latest_merge_verdicts } from './ResolverMergeCard';

const SEVERITY_COLOR = {
    CRITICAL: '#c0392b',
    HIGH: '#e67e22',
    MEDIUM: '#f1c40f',
    LOW: '#3498db',
    INFO: '#95a5a6',
};
const SEVERITY_OPTIONS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];

// Embedded ProposedSolution status colors. Mirrors the lifecycle in
// apps/api/prisma/schema.prisma's ProposedSolutionStatus.
const SOLUTION_STATUS_COLOR = {
    PROPOSED: '#3498db',   // blue — awaiting decision
    APPROVED: '#16a085',   // teal — accepted, awaiting conversion
    CONVERTED: '#27ae60',  // green — actionable created
    DISMISSED: '#95a5a6',  // grey — explicitly declined
};
const EFFORT_IMPACT_COLOR = {
    LOW: '#27ae60',
    MEDIUM: '#f1c40f',
    HIGH: '#e67e22',
};

/**
 * Pick a display name for a chat message author. Used on SHARED finding chats
 * to label each turn. Falls back through name → email → short id. Returns "—"
 * for unknown ids (admins outside CS who don't have a CsExecutive row).
 */
function format_finding_author(user_id, authors) {
    if (!user_id) return '—';
    const a = authors && authors[user_id];
    if (!a) return user_id.slice(0, 8);
    const name = [a.firstName, a.lastName].filter(Boolean).join(' ').trim();
    if (name) return name;
    if (a.email) return a.email;
    return user_id.slice(0, 8);
}

/**
 * Human-readable confirmation for a chat tool that applied immediately. The
 * rep must always be able to SEE what the AI changed — the terse "applied:
 * set_finding_status" was too subtle (feedback: status changes felt silent).
 * Status changes in particular spell out the new status.
 */
function describe_applied_action(a) {
    if (!a || !a.tool) return 'applied a change';
    const r = a.result || {};
    switch (a.tool) {
        case 'set_finding_status':
            return `Status changed to ${r.statusLabel || r.status || 'updated'}`;
        case 'set_finding_severity':
            return `Severity changed to ${r.severity || 'updated'}`;
        case 'set_client_visible_phrasing':
            return 'Client-visible phrasing saved';
        case 'approve_proposed_solution':
            return 'Proposed solution approved';
        case 'dismiss_proposed_solution':
            return 'Proposed solution dismissed';
        case 'link_finding_to_ticket':
            return 'Linked to external ticket';
        default:
            return `applied: ${a.tool}`;
    }
}

// Lifecycle: OPEN (active, needs the rep) -> terminal MITIGATED / RESOLVED /
// DISMISSED (all "closed"). ACCEPTED was removed — an accepted finding is just
// an active (OPEN) one. The raw statuses still ship over the wire; the filter
// UX wraps them in two buckets (ACTIVE / ARCHIVE) below.

// Bucket map for the 2-chip filter. Drives both chip counts and row filtering.
// Reps think in "still needs me" (Active = OPEN) vs "closed" (Archive =
// mitigated/resolved/dismissed) — the granular terminal states all archive
// together; the exact one is captured on close via "Mark Done".
const STATUS_BUCKET = {
    OPEN: 'ACTIVE',
    // Terminal states (mitigated / resolved / dismissed) all archive together —
    // the rep frame is "still needs me" vs "closed", not the granular status.
    MITIGATED: 'ARCHIVE',
    RESOLVED: 'ARCHIVE',
    DISMISSED: 'ARCHIVE',
};
const BUCKET_LABEL = { ACTIVE: 'Active', ARCHIVE: 'Archive' };
const BUCKET_ORDER = ['ACTIVE', 'ARCHIVE'];

const Findings = ({ store_id }) => {
    const [rows, set_rows] = useState([]);
    // Bucket-level filter — 'ACTIVE' | 'ARCHIVE'. Default is ACTIVE since
    // that's the rep's daily triage frame ("what still needs me").
    const [filter, set_filter] = useState('ACTIVE');
    // Table sort — click a column header to sort by that field; click again to
    // reverse. Time columns default to newest-first, others ascending.
    const [sort_col, set_sort_col] = useState('severity');
    const [sort_dir, set_sort_dir] = useState(1); // 1 = asc, -1 = desc
    const toggle_sort = (col) => {
        if (col === sort_col) { set_sort_dir((d) => -d); return; }
        set_sort_col(col);
        set_sort_dir(col === 'createdAt' || col === 'lastSeenAt' ? -1 : 1);
    };
    const SEV_RANK = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3, INFO: 4 };
    const [err, set_err] = useState(null);
    const [busy, set_busy] = useState(false);
    const [selected, set_selected] = useState(null);
    const [evidence_open, set_evidence_open] = useState(null); // the evidence object being expanded

    // After a chat tool changes a finding's state, the panel calls back
    // here to surgically refresh the selected row + the table row without
    // a full reload. Triggered by ChatTriagePanel.
    const refresh_one_finding = useCallback(async (finding_id) => {
        try {
            const next = await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/findings/${finding_id}`,
            });
            if (!next || !next.id) return;
            set_rows((curr) => curr.map((r) => (r.id === finding_id ? { ...r, ...next } : r)));
            set_selected((curr) => (curr && curr.id === finding_id ? { ...curr, ...next } : curr));
        } catch (e) {
            // Non-critical — the table will resync on next load() anyway.
        }
    }, [store_id]);

    // Load the full row set once and filter client-side by bucket. We need
    // every row in memory to compute chip counts (Active 30 / Done 15 /
    // Skipped 2) — pulling a server-filtered slice per chip would hide rows
    // that switched buckets after a chat-tool mutation. The backend caps at
    // 200 rows by default which is more than enough for one client.
    const load = useCallback(async () => {
        set_err(null);
        try {
            const resp = await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/findings`,
            });
            set_rows(Array.isArray(resp) ? resp : []);
        } catch (e) {
            set_err(e.message || String(e));
        }
    }, [store_id]);

    useEffect(() => {
        load();
    }, [load]);

    const update_finding = async (id, patch) => {
        set_busy(true);
        try {
            const next = await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/findings/${id}`,
                method: 'PATCH',
                body: patch,
            });
            // Surgical row update — no full reload, keeps the selected card stable
            set_rows((curr) => curr.map((r) => (r.id === id ? { ...r, ...next } : r)));
            if (selected && selected.id === id) {
                set_selected({ ...selected, ...next });
            }
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_busy(false);
        }
    };

    // Derived: bucket counts (chip labels) + the visible row slice. Computed
    // off the loaded `rows` so any chat-tool mutation that calls
    // refresh_one_finding rebuckets reactively.
    const bucket_counts = { ACTIVE: 0, ARCHIVE: 0 };
    for (const r of rows) {
        const b = STATUS_BUCKET[r.status];
        if (b) bucket_counts[b] += 1;
    }
    // Two buckets only — Active (OPEN) / Archive (closed). No "All".
    const visible_rows = rows.filter((r) => STATUS_BUCKET[r.status] === filter).slice().sort((a, b) => {
        const val = (r) => {
            switch (sort_col) {
                case 'module': return (r.module || '').toLowerCase();
                case 'title': return (r.title || '').toLowerCase();
                case 'rep': return r.repTriagedAt ? 1 : 0; // triaged vs needs-review
                case 'createdAt': return r.createdAt ? new Date(r.createdAt).getTime() : 0;
                case 'lastSeenAt': return r.lastSeenAt ? new Date(r.lastSeenAt).getTime() : 0;
                default: return SEV_RANK[r.severity] != null ? SEV_RANK[r.severity] : 99; // severity
            }
        };
        const av = val(a);
        const bv = val(b);
        if (av < bv) return -sort_dir;
        if (av > bv) return sort_dir;
        return 0;
    });

    // Layout (a) — slimmer table column when a Finding is selected, so the
    // detail drawer can grow to ~720px without stealing all the screen. When
    // Drawer overlay: list stays at full width; selected-finding card
    // floats on top, anchored to the right edge of the list container.
    // No more list-shrinking-and-jumping when selection changes.
    return (
        <div style={{ position: 'relative' }}>
            <Card style={{ minWidth: 0 }}>
                <CardBody>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div>
                            {BUCKET_ORDER.map((b) => (
                                <Button
                                    key={b}
                                    size='sm'
                                    outline={filter !== b}
                                    color='primary'
                                    onClick={() => set_filter(b)}
                                    style={{ marginRight: 4 }}
                                >
                                    {BUCKET_LABEL[b]} {bucket_counts[b]}
                                </Button>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{ fontSize: 11, color: '#8a94a6' }}>click a column header to sort</span>
                            <Button size='sm' onClick={load}>Refresh</Button>
                        </div>
                    </div>
                    {err ? <div style={{ color: '#c0392b', marginBottom: 8 }}>{err}</div> : null}
                    <Table size='sm' hover>
                        <thead>
                            <tr style={{ userSelect: 'none' }}>
                                {[
                                    { key: 'severity', label: 'Sev' },
                                    { key: 'module', label: 'Module' },
                                    { key: 'title', label: 'Title' },
                                    { key: 'rep', label: 'Rep' },
                                    { key: 'createdAt', label: 'Created' },
                                    { key: 'lastSeenAt', label: 'Last seen' },
                                ].map((c) => (
                                    <th
                                        key={c.key}
                                        onClick={() => toggle_sort(c.key)}
                                        title='Sort by this column'
                                        style={{ cursor: 'pointer', whiteSpace: 'nowrap', color: sort_col === c.key ? '#2f6fed' : undefined }}
                                    >
                                        {c.label}{sort_col === c.key ? (sort_dir === 1 ? ' ▲' : ' ▼') : ''}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {visible_rows.length === 0 ? (
                                <tr><td colSpan='6' style={{ color: '#7f8c8d', textAlign: 'center' }}>No findings</td></tr>
                            ) : visible_rows.map((r) => (
                                <tr
                                    key={r.id}
                                    onClick={() => set_selected(r)}
                                    style={{ cursor: 'pointer', background: selected && selected.id === r.id ? '#f4f6f8' : undefined }}
                                >
                                    <td>
                                        <Badge style={{ backgroundColor: SEVERITY_COLOR[r.severity] || '#95a5a6', color: '#fff' }}>
                                            {r.severity}
                                        </Badge>
                                    </td>
                                    <td>{r.module}</td>
                                    <td>{r.title}</td>
                                    <td>
                                        {/* Triage indicator. OPEN + no repTriagedAt = needs review */}
                                        {r.repTriagedAt ? (
                                            <Badge color='success' style={{ fontWeight: 400 }} title={`Triaged ${fmt_datetime(r.repTriagedAt)}`}>
                                                triaged
                                            </Badge>
                                        ) : r.status === 'OPEN' ? (
                                            <Badge color='warning' style={{ fontWeight: 400 }}>needs review</Badge>
                                        ) : null}
                                        {r.repNote ? (
                                            <span title={r.repNote} style={{ marginLeft: 4, fontSize: 12, color: '#27ae60' }}>📝</span>
                                        ) : null}
                                        {r.proposedSolution ? (
                                            <span
                                                title={`Proposed solution: ${r.proposedSolution.status}`}
                                                style={{ marginLeft: 4, fontSize: 11, color: SOLUTION_STATUS_COLOR[r.proposedSolution.status] || '#7f8c8d' }}
                                            >
                                                💡{r.proposedSolution.status === 'PROPOSED' ? '' : `·${r.proposedSolution.status[0]}`}
                                            </span>
                                        ) : null}
                                    </td>
                                    <td title={r.createdAt ? fmt_datetime(r.createdAt) : ''}>
                                        {r.createdAt ? fmt_date(r.createdAt) : '—'}
                                    </td>
                                    <td>{fmt_date(r.lastSeenAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </CardBody>
            </Card>

            {selected ? (
                <FindingDetailCard
                    selected={selected}
                    store_id={store_id}
                    busy={busy}
                    update_finding={update_finding}
                    set_selected={set_selected}
                    set_rows={set_rows}
                    set_evidence_open={set_evidence_open}
                    refresh_one_finding={refresh_one_finding}
                />
            ) : null}

            <EvidenceModal
                open={!!evidence_open}
                on_close={() => set_evidence_open(null)}
                store_id={store_id}
                evidence={evidence_open}
            />
        </div>
    );
};

const Section = ({ label, value }) => (
    <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
        <div style={{ fontSize: 13, color: '#2c3e50', marginTop: 2 }}>{value || <span style={{ color: '#7f8c8d' }}>—</span>}</div>
    </div>
);

const TAB_ORDER_BASE = ['triage', 'context', 'history'];
const TAB_LABEL = {
    triage: 'AI Chat',
    context: 'Context',
    solution: 'Solution',
    history: 'History',
};
// Solution tab is conditional — only present when the finding has a
// proposedSolution. In the new chat-driven flow it's a secondary surface
// (the rep mostly converses in the AI Chat tab); we split it out so the
// chat tab is unobstructed.
function tab_order_for(selected) {
    if (selected && selected.proposedSolution) {
        return ['triage', 'context', 'solution', 'history'];
    }
    return TAB_ORDER_BASE;
}

/**
 * Per-Finding detail card. Iteration-2 layout:
 *
 *   header: [severity badge] · MODULE · STATUS · key=<fingerprint>  ×
 *   tab bar: [Triage] [Context] [History]
 *
 *   Triage (default):
 *     - Severity dropdown
 *     - Proposed Solution panel (Convert button stays; Approve/Dismiss in chat)
 *     - 4 quick-reply chips + AI Chat with Send dropdown ("Send to AI" / "Just note")
 *   Context:
 *     - Observation · Mechanism · Evidence (drillable) · Resolution note
 *   History:
 *     - Inline FindingHistoryList (the old modal body, now a tab)
 *
 * Dropped vs previous iteration: 🕐 button on header, unified Notes section
 * + its radio + state. External phrasing now lives behind the
 * set_client_visible_phrasing chat tool.
 */
/**
 * Per-finding detail surface. By default renders as the per-client side-
 * anchored drawer (position:fixed Card). Pass `chromeless` to get just the
 * inner content — used by the portfolio-page modal which provides its own
 * Modal chrome via reactstrap.
 *
 * Exported so the portfolio /home/findings page can mount the same detail
 * surface as a modal (with chromeless=true) without duplicating the tabs +
 * chat + context + history wiring.
 */
export const FindingDetailCard = ({
    selected,
    store_id,
    busy,
    update_finding,
    set_selected,
    set_rows,
    set_evidence_open,
    refresh_one_finding,
    chromeless = false,
}) => {
    // Imperative handle into FindingChatPanel so the quick-reply chips above
    // the chat can fire prefilled prompts without duplicating send/render
    // logic. The chat panel is the source of truth for chat state.
    const chat_ref = useRef(null);
    const [active_tab, set_active_tab] = useState('triage');

    // "Mark Done" close flow. Clicking the button asks the AI to classify the
    // close (mitigated/resolved/dismissed) + draft a closing comment, then
    // opens a modal prefilled with both (editable) for the rep to confirm.
    const [close_open, set_close_open] = useState(false);
    const [close_busy, set_close_busy] = useState(false);
    const [close_init, set_close_init] = useState({ suggestedStatus: 'MITIGATED', draftComment: '' });
    const open_mark_done = useCallback(async () => {
        set_close_busy(true);
        set_close_open(true);
        try {
            const s = await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/findings/${selected.id}/suggest-close`,
                method: 'POST',
                body: {},
            });
            set_close_init({
                suggestedStatus: (s && s.suggestedStatus) || 'MITIGATED',
                draftComment: (s && s.draftComment) || '',
            });
        } catch (e) {
            set_close_init({ suggestedStatus: 'MITIGATED', draftComment: '' });
        } finally {
            set_close_busy(false);
        }
    }, [store_id, selected.id]);

    // Chat-proposed status change → open the SAME "Mark finding done" confirmation
    // the ✓ button uses, pre-filled with the AI's suggested status + note. The
    // rep's click applies it (the chat never applies status itself — that's what
    // caused the "said done but wasn't" over-claim). Reopen (OPEN) is safe +
    // reversible, so it applies directly rather than through the close modal.
    const on_status_draft = useCallback((d) => {
        if (!d || !d.status) return;
        if (d.status === 'OPEN') {
            update_finding(selected.id, { status: 'OPEN' });
            return;
        }
        set_close_init({ suggestedStatus: d.status, draftComment: d.note || '' });
        set_close_open(true);
    }, [selected.id, update_finding]);

    // Rep roster for the task-create modal's owner select (fetched once).
    const [reps, set_reps] = useState([]);
    const [rep_role, set_rep_role] = useState('rep');
    const [rep_self, set_rep_self] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await cs_analysis_fetch({ path: '/api/v1/cs/reps' });
                if (cancelled) return;
                set_reps(r && Array.isArray(r.reps) ? r.reps : []);
                set_rep_role(r && r.role ? r.role : 'rep');
                set_rep_self(r && r.self ? r.self : null);
            } catch (_e) { /* non-fatal — owner select still works, unpopulated */ }
        })();
        return () => { cancelled = true; };
    }, []);

    // Chat-proposed TASK → open the shared TaskCreateModal pre-filled. The modal
    // state lives HERE (parent), not in the chat panel, so a post-send re-render
    // of the chat can't drop it (that was the flicker — child-held modal state
    // got reset while the parent-held mark-done modal survived).
    const [task_draft, set_task_draft] = useState(null);
    const on_task_draft = useCallback((payload) => {
        if (payload) set_task_draft(payload);
    }, []);

    // Reset to Triage whenever the rep clicks a different Finding row. The
    // common case after switching items is "what's the AI saying about THIS
    // one?", not "show me audit history of the new one".
    useEffect(() => {
        set_active_tab('triage');
    }, [selected.id]);

    // Finding history lives HERE (not in HistoryTab) so the History tab label
    // can carry the "n merged evidence to review" hint before the rep opens
    // the tab. Fetched once when the drawer lands on a finding — the hint only
    // moves when the nightly resolver merges (a fresh drawer open) or when the
    // rep records a verdict (the card reloads explicitly) — and HistoryTab
    // refetches on mount so the ledger itself is fresh when opened. The
    // sequence guard drops a reply that lands after the rep has switched to
    // another finding.
    const [history_rows, set_history_rows] = useState([]);
    const [history_loading, set_history_loading] = useState(false);
    const [history_err, set_history_err] = useState(null);
    const history_seq = useRef(0);
    const load_history = useCallback(async () => {
        const seq = ++history_seq.current;
        set_history_loading(true);
        set_history_err(null);
        try {
            const resp = await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/findings/${selected.id}/history`,
            });
            if (seq !== history_seq.current) return;
            set_history_rows(Array.isArray(resp) ? resp : []);
        } catch (e) {
            if (seq !== history_seq.current) return;
            set_history_err(e.message || String(e));
        } finally {
            if (seq === history_seq.current) set_history_loading(false);
        }
    }, [store_id, selected.id]);
    useEffect(() => {
        set_history_rows([]);
    }, [selected.id]);
    useEffect(() => {
        load_history();
    }, [load_history]);
    const history = { rows: history_rows, loading: history_loading, err: history_err, reload: load_history };
    const undecided_merges = count_undecided_merges(history_rows);

    // Fold a finding view (the shape GET :id returns) into the drawer + the
    // list row, the way refresh_one_finding does — used by the History tab's
    // detach / confirm verdicts, whose endpoints return the updated view so no
    // second GET is needed. Functional updates on purpose: the portfolio modal
    // passes stubs that ignore non-null values, and those stay no-ops.
    const apply_finding_view = useCallback((next) => {
        if (!next || !next.id) return;
        set_rows((curr) => (Array.isArray(curr) ? curr.map((r) => (r.id === next.id ? { ...r, ...next } : r)) : curr));
        set_selected((curr) => (curr && curr.id === next.id ? { ...curr, ...next } : curr));
    }, [set_rows, set_selected]);

    const send_to_chat = (content) => {
        if (chat_ref.current && typeof chat_ref.current.send_text === 'function') {
            chat_ref.current.send_text(content);
        }
    };

    // Quick-reply chips. The 5th — Draft client-visible version — was added
    // alongside the new set_client_visible_phrasing chat tool. We KEPT it
    // because the natural-language route ("how should I phrase this to the
    // customer?") otherwise has no one-click affordance; reps will skip it.
    const quick_replies = [
        {
            label: 'Skip',
            on_click: () => send_to_chat('skip this — false positive'),
        },
        {
            label: 'Why this severity?',
            on_click: () =>
                send_to_chat(
                    `Why is this severity ${selected.severity}? Should it be lower or higher?`,
                ),
        },
        {
            label: 'Show source',
            on_click: () => send_to_chat('Show me the source messages this came from'),
        },
        {
            label: 'Link external ticket…',
            on_click: () => {
                const id = (typeof window !== 'undefined' && window.prompt)
                    ? window.prompt('External ticket ID (e.g. JIRA-123):')
                    : '';
                if (id && id.trim()) {
                    send_to_chat(`Link this finding to external ticket ${id.trim()}`);
                }
            },
        },
        {
            label: 'Draft client-visible version',
            on_click: () =>
                send_to_chat(
                    'Please draft a client-visible version of this finding and propose it for me to review.',
                ),
        },
    ];

    // Chrome-free inner content. Both the per-client drawer (Card with
    // position:fixed) and the portfolio-page modal (ModalBody) wrap this.
    const body = (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }}>
            {/* Header row — read-only metadata badge + close. Severity
                edit moved to the Context tab so the header stays calm. */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                    <Badge style={{ backgroundColor: SEVERITY_COLOR[selected.severity], color: '#fff' }}>
                        {selected.severity}
                    </Badge>
                    <span style={{ color: '#7f8c8d', fontSize: 12 }}>
                        {selected.module} · {selected.status} · key=<code>{selected.fingerprint}</code>
                    </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {selected.status === 'OPEN' ? (
                        <button
                            type='button'
                            onClick={open_mark_done}
                            title='Close this finding (AI drafts a closing comment + classification)'
                            style={{
                                fontSize: 12, fontWeight: 500, padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
                                border: '1px solid #1e8f5b', background: '#fff', color: '#1e8f5b',
                            }}
                        >
                            ✓ Mark done
                        </button>
                    ) : null}
                    <button
                        type='button'
                        onClick={() => set_selected(null)}
                        title='Close'
                        aria-label='Close'
                        style={{
                            width: 30, height: 30, borderRadius: 6, border: '1px solid #e1e6ec',
                            background: '#f5f7fa', color: '#57606f', fontSize: 18, lineHeight: 1, cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}
                    >
                        ×
                    </button>
                </div>
            </div>

            {/* Title under the header so it can wrap freely. */}
            <div style={{ marginTop: 6, fontWeight: 600, fontSize: 15 }}>{selected.title}</div>

            <CloseFindingModal
                open={close_open}
                loading={close_busy}
                store_id={store_id}
                finding={selected}
                init={close_init}
                on_close={() => set_close_open(false)}
                on_done={() => {
                    set_close_open(false);
                    if (typeof refresh_one_finding === 'function') refresh_one_finding(selected.id);
                    set_selected(null); // finding moved to Archive → close the drawer/modal
                }}
            />

            {/* Chat-proposed task → shared Create-Task modal (parent-held state so
                it survives chat re-renders). */}
            <TaskCreateModal
                open={!!task_draft}
                prefill={task_draft}
                store_id={store_id}
                reps={reps}
                rep_role={rep_role}
                self_email={rep_self ? rep_self.email : ''}
                on_close={() => set_task_draft(null)}
                on_created={() => {
                    set_task_draft(null);
                    if (typeof refresh_one_finding === 'function') refresh_one_finding(selected.id);
                }}
            />

            {/* Tab bar — hand-rolled to avoid pulling in another reactstrap
                Tab component and to keep styles inline with the rest of
                this file. Active tab gets a 2px primary underline. */}
            <div
                role='tablist'
                style={{
                    marginTop: 12,
                    borderBottom: '1px solid #ecf0f1',
                    display: 'flex',
                    gap: 4,
                }}
            >
                {tab_order_for(selected).map((t) => {
                    const is_active = active_tab === t;
                    return (
                        <button
                            key={t}
                            type='button'
                            role='tab'
                            aria-selected={is_active}
                            onClick={() => set_active_tab(t)}
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: '8px 14px',
                                fontSize: 13,
                                fontWeight: is_active ? 600 : 400,
                                color: is_active ? '#2c3e50' : '#7f8c8d',
                                borderBottom: is_active ? '2px solid #3498db' : '2px solid transparent',
                                marginBottom: -1,
                                cursor: 'pointer',
                            }}
                        >
                            {TAB_LABEL[t]}
                            {t === 'history' && undecided_merges > 0 ? (
                                <span
                                    title='The nightly issue resolver merged evidence from new candidates into this finding — confirm or detach each one in History'
                                    style={{
                                        marginLeft: 6, fontSize: 10, fontWeight: 600, color: '#8a5a00', background: '#fff3cd',
                                        borderRadius: 8, padding: '1px 6px', whiteSpace: 'nowrap', verticalAlign: 'middle',
                                    }}
                                >
                                    {undecided_merges} merged evidence to review
                                </span>
                            ) : null}
                        </button>
                    );
                })}
            </div>

            {/* Tab content area fills the rest of the drawer (flex: 1 +
                minHeight: 0 so children can shrink + scroll). Triage flex-
                fills via its own chain; other tabs wrap in an overflow-auto
                div so they scroll inside the drawer rather than clipping. */}
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {/* Triage (the chat) stays MOUNTED across tab switches — only its
                visibility toggles. Conditionally rendering it (as the other
                tabs are) would unmount FindingChatPanel every time the rep
                peeks at Solution/Context and back, discarding the unsent draft
                and forcing a full chat reload. Keeping it mounted preserves the
                draft, scroll position, and loaded messages. */}
            <div
                style={{
                    flex: 1,
                    minHeight: 0,
                    display: active_tab === 'triage' ? 'flex' : 'none',
                    flexDirection: 'column',
                }}
            >
                <TriageTab
                    selected={selected}
                    quick_replies={quick_replies}
                    chat_ref={chat_ref}
                    store_id={store_id}
                    refresh_one_finding={refresh_one_finding}
                    on_status_draft={on_status_draft}
                    on_task_draft={on_task_draft}
                />
            </div>

            {active_tab === 'context' ? (
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    <ContextTab
                        selected={selected}
                        set_evidence_open={set_evidence_open}
                        busy={busy}
                        update_finding={update_finding}
                    />
                </div>
            ) : null}

            {active_tab === 'solution' && selected.proposedSolution ? (
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    <SolutionTab
                        selected={selected}
                        store_id={store_id}
                        set_selected={set_selected}
                        set_rows={set_rows}
                    />
                </div>
            ) : null}

            {active_tab === 'history' ? (
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    <HistoryTab store_id={store_id} selected={selected} history={history} on_view={apply_finding_view} />
                </div>
            ) : null}
            </div>
        </div>
    );

    // Portfolio caller wants the chrome-free body to wrap in its own Modal.
    // Per-client (default) wraps in a position:fixed Card with shadow.
    if (chromeless) return body;

    return (
        <Card
            style={{
                // Fixed (not absolute) so the drawer stays pinned in the
                // viewport as the underlying list scrolls. Without `position:
                // fixed` the drawer scrolled away with the page.
                position: 'fixed',
                // Both top and bottom anchored: drawer fills the available
                // viewport height (minus the global nav at top and a small
                // gutter at bottom).
                top: 90,
                bottom: 20,
                right: 20,
                width: '60vw',
                minWidth: 600,
                maxWidth: 1100,
                overflow: 'hidden',
                boxShadow: '-6px 0 24px rgba(0,0,0,0.18)',
                zIndex: 1050,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <CardBody style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                {body}
            </CardBody>
        </Card>
    );
};

/**
 * Triage tab — the daily-driver surface. Severity + Proposed Solution at
 * the top so direct actions don't require scrolling, then the chat. Notes
 * are gone in iteration 2 — externalPhrasing is set via the chat tool.
 */
const TriageTab = ({
    selected,
    quick_replies,
    chat_ref,
    store_id,
    refresh_one_finding,
    on_status_draft,
    on_task_draft,
}) => {
    return (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6, flexShrink: 0 }}>
                {quick_replies.map((q) => (
                    <Button
                        key={q.label}
                        size='sm'
                        outline
                        color='secondary'
                        onClick={q.on_click}
                        style={{ fontSize: 12, padding: '2px 8px' }}
                    >
                        {q.label}
                    </Button>
                ))}
            </div>
            <FindingChatPanel
                ref={chat_ref}
                finding_id={selected.id}
                store_id={store_id}
                on_state_changed={refresh_one_finding}
                on_status_draft={on_status_draft}
                on_task_draft={on_task_draft}
            />
        </div>
    );
};

/**
 * Solution tab — only rendered when a proposedSolution exists. Splitting
 * it out keeps Triage focused on the rep's primary work surface (chat).
 * Reps land here when they're ready to act on the AI's recommendation.
 */
const SolutionTab = ({ selected, store_id, set_selected, set_rows }) => {
    return (
        <div style={{ marginTop: 12 }}>
            <ProposedSolutionPanel
                store_id={store_id}
                solution={selected.proposedSolution}
                on_changed={(updated) => {
                    const nextFinding = { ...selected, proposedSolution: updated };
                    set_selected(nextFinding);
                    set_rows((curr) =>
                        curr.map((r) =>
                            r.id === selected.id ? { ...r, proposedSolution: updated } : r,
                        ),
                    );
                }}
            />
        </div>
    );
};

/**
 * Context tab — read-only descriptive surface. The data the LLM emitted +
 * any resolution note the rep / chat tool persisted.
 */
const ContextTab = ({ selected, set_evidence_open, busy, update_finding }) => {
    return (
        <div>
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Severity
                </div>
                <Input
                    type='select'
                    bsSize='sm'
                    value={selected.severity}
                    disabled={busy}
                    onChange={(e) => {
                        if (e.target.value !== selected.severity) {
                            update_finding(selected.id, { severity: e.target.value });
                        }
                    }}
                    style={{ width: 130, fontSize: 13 }}
                >
                    {SEVERITY_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </Input>
                {selected.repTriagedAt ? (
                    <span style={{ fontSize: 11, color: '#7f8c8d' }}>
                        last triaged by {selected.repTriagedBy || 'rep'} ·{' '}
                        {fmt_datetime(selected.repTriagedAt)}
                    </span>
                ) : null}
            </div>
            <Section label='Observation' value={selected.observation} />
            <Section label='Mechanism' value={selected.mechanism} />
            <Section
                label='Evidence'
                value={
                    Array.isArray(selected.evidence) && selected.evidence.length
                        ? selected.evidence.map((e, i) => {
                              const drillable = !!e.ingestionId;
                              return (
                                  <div
                                      key={i}
                                      onClick={() => drillable && set_evidence_open(e)}
                                      style={{
                                          marginBottom: 6,
                                          fontSize: 12,
                                          cursor: drillable ? 'pointer' : 'default',
                                          padding: '4px 6px',
                                          borderRadius: 3,
                                          border: drillable ? '1px solid transparent' : undefined,
                                      }}
                                      onMouseEnter={(ev) => {
                                          if (drillable) ev.currentTarget.style.background = '#f4f6f8';
                                      }}
                                      onMouseLeave={(ev) => {
                                          if (drillable) ev.currentTarget.style.background = 'transparent';
                                      }}
                                  >
                                      <strong>{e.source}</strong>
                                      {e.occurredAt ? ' · ' + fmt_datetime(e.occurredAt) : ''}
                                      {drillable ? (
                                          <span style={{ color: '#3498db', marginLeft: 6, fontSize: 11 }}>view →</span>
                                      ) : null}
                                      {e.quote ? (
                                          <div style={{ color: '#34495e', fontStyle: 'italic' }}>
                                              “{e.quote.slice(0, 240)}”
                                          </div>
                                      ) : null}
                                  </div>
                              );
                          })
                        : <span style={{ color: '#7f8c8d' }}>—</span>
                }
            />
            {selected.resolutionNote ? (
                <Section label='Resolution note' value={selected.resolutionNote} />
            ) : null}
            {selected.externalPhrasing ? (
                <Section label='Client-visible phrasing' value={selected.externalPhrasing} />
            ) : null}
        </div>
    );
};

/**
 * History tab — inline replacement for the modal previously triggered by 🕐.
 * Shows the FindingHistory rows reverse-chrono. The rows are owned by
 * FindingDetailCard (`history`), which fetches them when the drawer opens so
 * the tab label can flag undecided resolver merges; this tab refetches on
 * mount so what the rep sees on opening it is fresh.
 *
 * Resolver merges (`prevState.kind === 'resolver_evidence_added'`) render as
 * a ResolverMergeCard with the ✓ Same issue / ✗ Not the same issue chips;
 * every other row — including pre-resolver `evidence_added` rows without a
 * `kind` — is the plain ledger entry. `on_view` receives the finding view
 * the verdict endpoints return so the drawer's evidence count follows.
 */
const HistoryTab = ({ store_id, selected, history, on_view }) => {
    const { rows, loading, err, reload } = history;

    // Fresh-on-open. The parent resets active_tab to Triage on selected.id
    // change, so this fires when the rep actually opens the History tab.
    useEffect(() => {
        reload();
    }, [reload]);

    // Revert intentionally removed in the chat-driven flow. History is a
    // frozen, append-only audit ledger; any "I want to undo that" intent
    // goes through the chat (the rep says it; the LLM calls
    // set_finding_status / set_finding_severity / etc. to restore prior
    // state). Keeps the audit trail honest — every state change has a
    // first-class actor + reason, no out-of-band rewrites.

    // Spinner only while nothing is on screen yet — a verdict's refetch keeps
    // the rows (and the card that is mid-request) mounted.
    if (loading && rows.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: 24 }}>
                <Spinner size='sm' />
            </div>
        );
    }
    if (err) return <div style={{ color: '#c0392b', marginTop: 12 }}>{err}</div>;
    if (rows.length === 0) {
        return (
            <div style={{ color: '#7f8c8d', fontSize: 13, marginTop: 12 }}>
                No history entries yet. This finding hasn't been modified since it was first emitted.
            </div>
        );
    }
    const verdicts = latest_merge_verdicts(rows);
    return (
        <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: '#7f8c8d', marginBottom: 8 }}>
                Frozen ledger — every state change with actor + reason. To restore a prior state,
                ask the chat ("set status back to OPEN", "bump severity to HIGH again") so the
                action gets a first-class audit entry of its own.
            </div>
            {rows.map((h) => {
                if (is_resolver_merge(h)) {
                    return (
                        <ResolverMergeCard
                            key={h.id}
                            row={h}
                            verdict={verdicts[h.id]}
                            store_id={store_id}
                            finding_id={selected.id}
                            on_view={on_view}
                            reload_history={reload}
                        />
                    );
                }
                const prev = h.prevState || {};
                return (
                    <div
                        key={h.id}
                        style={{
                            padding: '8px 10px',
                            borderLeft: '2px solid #ecf0f1',
                            marginBottom: 6,
                            position: 'relative',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Badge color={h.changeType === 'reverted' ? 'warning' : 'info'} style={{ fontWeight: 400 }}>
                                {h.changeType}
                            </Badge>
                            <span style={{ fontSize: 12, color: '#7f8c8d' }}>
                                {fmt_datetime(h.changedAt)}
                                {h.byUserId ? ` · by ${h.byUserId}` : ' · analyzer'}
                            </span>
                        </div>
                        {h.note ? (
                            <div style={{ fontSize: 12, color: '#34495e', marginTop: 4, fontStyle: 'italic' }}>
                                {h.note}
                            </div>
                        ) : null}
                        <div style={{ fontSize: 11, color: '#7f8c8d', marginTop: 4 }}>
                            prev state: status={String(prev.status)}
                            {prev.severity ? `, severity=${prev.severity}` : ''}
                            {prev.repNote ? `, repNote="${String(prev.repNote).slice(0, 80)}…"` : ''}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

/**
 * Per-Finding chat panel — Phase B's primary triage UX. Inits a chat session
 * scoped to one Finding via POST /findings/:id/chat/init, then loops on
 * POST /chat/sessions/:sid/messages. The LLM has direct-apply tools
 * (set_finding_status, approve_proposed_solution, dismiss_proposed_solution,
 * link_finding_to_ticket) plus a staged propose_actionable_create.
 *
 * When a tool changes state, the server returns appliedActions[] on the
 * message-send response — we call on_state_changed so the parent table
 * resyncs the finding row.
 *
 * Reuses the styling vocabulary of ChatDrawer but inlined under the card
 * (no fixed positioning) so it lives inside the Finding's flow.
 */
const FindingChatPanel = forwardRef(({ finding_id, store_id, on_state_changed, on_status_draft, on_task_draft }, ref) => {
    const [session_id, set_session_id] = useState(null);
    const [messages, set_messages] = useState([]);
    // Per-message author info for SHARED threads. Map: { [userId]: { firstName,
    // lastName, email } } — returned by /findings/:id/chat/init + the messages
    // endpoint so we can label each turn without an extra round-trip.
    const [authors, set_authors] = useState({});
    const [draft, set_draft] = useState('');
    const [busy, set_busy] = useState(false);
    const [err, set_err] = useState(null);
    // AI-proposed tasks no longer open a modal *here*. The draft is handed up to
    // the parent (FindingDetailCard) via `on_task_draft`, which owns the single
    // TaskCreateModal — so this panel re-rendering after a send can't drop it.
    // Send-mode dropdown — 'AI' calls /messages and the LLM responds; 'NOTE'
    // POSTs to /notes and just appends the rep's USER message. The mode
    // persists across successive sends within this panel mount (in-memory
    // only, deliberately not localStorage). Default 'AI' since most uses
    // want the LLM response.
    const [send_mode, set_send_mode] = useState('AI');
    const [send_menu_open, set_send_menu_open] = useState(false);
    const scroll_ref = useRef(null);
    const send_menu_ref = useRef(null);
    const input_ref = useRef(null);

    // Keep the chat input focused so the rep can type continuously: on open once
    // the session is ready, and again after each send (the textarea disables
    // while busy — which blurs it — so it needs re-focusing when busy clears).
    useEffect(() => {
        if (!busy && session_id && input_ref.current) input_ref.current.focus();
    }, [busy, session_id]);

    // Close the send-mode dropdown on outside click. Simple document handler
    // since reactstrap's Dropdown would bring its own button styling that
    // doesn't compose well with the split Send/▾ pattern.
    useEffect(() => {
        if (!send_menu_open) return undefined;
        const on_doc_click = (e) => {
            if (send_menu_ref.current && !send_menu_ref.current.contains(e.target)) {
                set_send_menu_open(false);
            }
        };
        document.addEventListener('mousedown', on_doc_click);
        return () => document.removeEventListener('mousedown', on_doc_click);
    }, [send_menu_open]);

    // Re-init the chat whenever the selected finding changes. The server
    // reuses an existing session for (user, finding) so this is idempotent.
    useEffect(() => {
        let cancelled = false;
        if (!finding_id) return undefined;
        set_session_id(null);
        set_messages([]);
        set_err(null);
        // Clear any unsent draft when moving to a different finding so a draft
        // typed for finding A never bleeds into finding B. (Within a single
        // finding the panel stays mounted across tab switches — see the
        // display-toggle in FindingDetailCard — so the draft survives there.)
        set_draft('');
        (async () => {
            try {
                const resp = await cs_analysis_fetch({
                    path: `/api/v1/findings/${encodeURIComponent(finding_id)}/chat/init`,
                    method: 'POST',
                    body: {},
                });
                if (cancelled) return;
                set_session_id(resp.sessionId);
                set_messages(Array.isArray(resp.messages) ? resp.messages : []);
                set_authors(resp.authors || {});
            } catch (e) {
                if (!cancelled) set_err(e.message || String(e));
            }
        })();
        return () => { cancelled = true; };
    }, [finding_id]);

    useEffect(() => {
        if (scroll_ref.current) {
            scroll_ref.current.scrollTop = scroll_ref.current.scrollHeight;
        }
    }, [messages, busy]);

    // Core send — accepts content directly so quick-reply chips in the
    // parent can fire prefilled prompts without touching the draft textarea.
    // The original `send` (Enter / Send button) wraps this by passing
    // `draft.trim()`. Send/render logic itself is unchanged; only the
    // content-source seam was factored out.
    const send_text = async (content) => {
        if (!content || busy || !session_id) return;
        set_busy(true);
        set_err(null);
        const optimistic = {
            id: 'tmp-' + Date.now(),
            role: 'USER',
            content,
            createdAt: new Date().toISOString(),
        };
        set_messages((prev) => [...prev, optimistic]);
        try {
            const resp = await cs_analysis_fetch({
                path: `/api/v1/chat/sessions/${session_id}/messages`,
                method: 'POST',
                body: { content, findingId: finding_id },
            });
            // Merge the sender's display info so the just-sent turn resolves to
            // a name (a fresh session's initial load returns no authors).
            if (resp.authors) set_authors((prev) => ({ ...prev, ...resp.authors }));
            set_messages((prev) => {
                const without_tmp = prev.filter((m) => m.id !== optimistic.id);
                return [
                    ...without_tmp,
                    {
                        id: resp.userMessageId,
                        role: 'USER',
                        userId: resp.userMessageUserId,
                        content,
                        createdAt: new Date().toISOString(),
                    },
                    {
                        id: resp.assistantMessageId,
                        role: 'ASSISTANT',
                        content: resp.reply,
                        citations: resp.corpusCitations || [],
                        createdAt: new Date().toISOString(),
                        appliedActions: resp.appliedActions || [],
                    },
                ];
            });
            // If a state-changing tool fired, refresh the parent finding row.
            const applied = Array.isArray(resp.appliedActions) ? resp.appliedActions : [];
            const state_changing = applied.some((a) =>
                a && a.tool && (
                    a.tool === 'set_finding_status' ||
                    a.tool === 'set_finding_severity' ||
                    a.tool === 'set_client_visible_phrasing' ||
                    a.tool === 'approve_proposed_solution' ||
                    a.tool === 'dismiss_proposed_solution' ||
                    a.tool === 'link_finding_to_ticket'
                )
            );
            if (state_changing && typeof on_state_changed === 'function') {
                on_state_changed(finding_id);
            }
            // When the AI proposes a task, pop the pre-filled create modal so the
            // rep can review/edit and confirm. The finding chat is always a
            // per-client session, so propose_actionable_create now returns a live
            // `taskDraft` inline (no ChatProposal staging — reps never applied the
            // staged cards). Open the modal straight from the draft; the older
            // staged-proposal path is kept as a fallback for any other kind.
            if (Array.isArray(resp.taskDrafts) && resp.taskDrafts.length > 0) {
                // Hand the draft UP to the parent, which owns the TaskCreateModal
                // state — so this chat panel re-rendering after send can't drop it.
                if (typeof on_task_draft === 'function') on_task_draft(resp.taskDrafts[0]);
            } else if (resp.proposalCount && resp.proposalCount > 0) {
                await maybe_open_actionable_modal();
            }
            // Chat proposed a finding status change → open the "Mark finding done"
            // confirmation pre-filled. The rep's click applies it; the chat never
            // changes status itself (kills the "said done but wasn't" over-claim).
            if (Array.isArray(resp.statusDrafts) && resp.statusDrafts.length > 0 && typeof on_status_draft === 'function') {
                on_status_draft(resp.statusDrafts[0]);
            }
        } catch (e) {
            set_messages((prev) => prev.filter((m) => m.id !== optimistic.id));
            set_err(e.message || String(e));
        } finally {
            set_busy(false);
        }
    };

    // Fetch the session's staged proposals and, if the newest is an
    // actionable_create, open the pre-filled modal. Only actionable_create is
    // surfaced here; other proposal kinds don't arise in the finding chat.
    const maybe_open_actionable_modal = async () => {
        if (!session_id) return;
        try {
            const proposals = await cs_analysis_fetch({
                path: `/api/v1/chat/sessions/${session_id}/proposals`,
            });
            const list = Array.isArray(proposals) ? proposals : [];
            // Newest pending actionable_create wins (the one just staged).
            const match = [...list].reverse().find(
                (p) => p && p.kind === 'actionable_create' && p.status !== 'DISMISSED' && p.status !== 'APPLIED',
            );
            if (match && typeof on_task_draft === 'function') on_task_draft(match.payload);
        } catch (_e) {
            // Non-fatal — the proposal still exists server-side; surfacing is best-effort.
        }
    };

    // "Just add as note" path — POST /notes persists a USER ChatMessage
    // without invoking the LLM. The endpoint returns the persisted row; we
    // append it to the local thread the same way we'd append an AI reply.
    // No state-change refresh path here (the backend doesn't run tools).
    const post_note = async (content) => {
        if (!content || busy || !session_id) return;
        set_busy(true);
        set_err(null);
        const optimistic = {
            id: 'tmp-' + Date.now(),
            role: 'USER',
            content,
            createdAt: new Date().toISOString(),
        };
        set_messages((prev) => [...prev, optimistic]);
        try {
            const resp = await cs_analysis_fetch({
                path: `/api/v1/chat/sessions/${session_id}/notes`,
                method: 'POST',
                body: { content, findingId: finding_id },
            });
            const saved = resp && resp.message;
            if (resp && resp.authors) set_authors((prev) => ({ ...prev, ...resp.authors }));
            set_messages((prev) => {
                const without_tmp = prev.filter((m) => m.id !== optimistic.id);
                if (!saved) return without_tmp;
                return [
                    ...without_tmp,
                    {
                        id: saved.id,
                        role: saved.role || 'USER',
                        userId: saved.userId,
                        content: saved.content,
                        createdAt: saved.createdAt || new Date().toISOString(),
                    },
                ];
            });
        } catch (e) {
            set_messages((prev) => prev.filter((m) => m.id !== optimistic.id));
            set_err(e.message || String(e));
        } finally {
            set_busy(false);
        }
    };

    const send = async () => {
        const content = draft.trim();
        if (!content || busy || !session_id) return;
        set_draft('');
        if (send_mode === 'NOTE') {
            await post_note(content);
        } else {
            await send_text(content);
        }
    };

    // Expose send_text to the parent so quick-reply chips above the chat
    // can prefill canonical prompts. Re-bind on busy/session changes so the
    // closed-over state in the imperative handle stays current.
    useImperativeHandle(ref, () => ({
        send_text: (content) => send_text(content),
        is_ready: () => Boolean(session_id) && !busy,
    }), [session_id, busy]);

    return (
        <div style={{ marginTop: 16, border: '1px solid #ecf0f1', borderRadius: 6, background: '#fafbfc', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '8px 10px', borderBottom: '1px solid #ecf0f1', fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5, flexShrink: 0 }}>
                AI triage chat
            </div>
            {err ? <div style={{ padding: '6px 10px', color: '#c0392b', fontSize: 12, flexShrink: 0 }}>{err}</div> : null}
            <div
                ref={scroll_ref}
                style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 10 }}
            >
                {messages.length === 0 && !busy && !err ? (
                    <div style={{ color: '#7f8c8d', fontSize: 12 }}>Loading triage starter…</div>
                ) : null}
                {messages.map((m) => (
                    <div key={m.id} style={{ marginBottom: 8, display: 'flex', flexDirection: 'column', alignItems: m.role === 'USER' ? 'flex-end' : 'flex-start' }}>
                        {/* Per-finding chat is SHARED (Batch B) — label each
                            USER turn with the author so reps can tell each
                            other's contributions apart. */}
                        {m.role === 'USER' && m.userId ? (
                            <div style={{ fontSize: 10, color: '#7f8c8d', marginBottom: 2, paddingRight: 4 }}>
                                {format_finding_author(m.userId, authors)}
                            </div>
                        ) : null}
                        <div style={{ maxWidth: '85%', padding: '6px 10px', borderRadius: 8, background: m.role === 'USER' ? '#e8f0fe' : '#ffffff', color: '#2c3e50', fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-wrap', border: m.role === 'USER' ? 'none' : '1px solid #ecf0f1' }}>
                            {m.content}
                            {Array.isArray(m.appliedActions) && m.appliedActions.length > 0 ? (
                                <div style={{ marginTop: 4, fontSize: 11, color: '#1e8449' }}>
                                    {m.appliedActions.map((a, i) => (
                                        <div key={i}>
                                            {a.result && a.result.error
                                                ? <span style={{ color: '#c0392b' }}>✗ {a.tool}: {a.result.error}</span>
                                                : <span>✓ {describe_applied_action(a)}</span>}
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                        {m.role === 'ASSISTANT' ? (
                            <CorpusSources citations={m.citations} ctx={{ scope: 'finding', clientId: store_id }} />
                        ) : null}
                    </div>
                ))}
                {busy ? <div style={{ color: '#7f8c8d', fontSize: 12 }}>Thinking…</div> : null}
            </div>
            <div style={{ borderTop: '1px solid #ecf0f1', padding: 8, display: 'flex', gap: 6, alignItems: 'stretch', flexShrink: 0 }}>
                <Input
                    innerRef={input_ref}
                    type='textarea'
                    rows={2}
                    placeholder={send_mode === 'NOTE'
                        ? 'Add a note to the thread (no AI reply)… (Enter to send)'
                        : 'Tell the AI what to do with this finding… (Enter to send)'}
                    value={draft}
                    onChange={(e) => set_draft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            send();
                        }
                    }}
                    disabled={busy || !session_id}
                    style={{ resize: 'none', fontSize: 13, flex: 1, minWidth: 0 }}
                />
                {/* Split Send button — the main half fires the current
                    send_mode; the ▾ half opens a small menu to switch modes.
                    The menu defaults to "Send to AI" and persists the rep's
                    choice across consecutive sends (in-memory only). */}
                <div ref={send_menu_ref} style={{ position: 'relative', display: 'flex', alignSelf: 'flex-end' }}>
                    <Button
                        color='primary'
                        onClick={send}
                        disabled={busy || !draft.trim() || !session_id}
                        style={{
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                            paddingRight: 10,
                            // Remove right border so the two buttons share a
                            // single edge — no visible vertical seam.
                            borderRight: 0,
                            marginRight: 0,
                        }}
                        title={send_mode === 'NOTE' ? 'Adds a note without invoking the AI' : 'Sends to the AI for a reply'}
                    >
                        {send_mode === 'NOTE' ? 'Just note' : 'Send'}
                    </Button>
                    <Button
                        color='primary'
                        onClick={() => set_send_menu_open((v) => !v)}
                        disabled={busy || !session_id}
                        style={{
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            // No borderLeft / no marginLeft — the buttons sit
                            // truly flush, looking like one continuous control
                            // with the same primary fill.
                            borderLeft: 0,
                            marginLeft: 0,
                            paddingLeft: 10,
                            paddingRight: 10,
                        }}
                        title='Send mode'
                        aria-haspopup='menu'
                        aria-expanded={send_menu_open}
                    >
                        ▾
                    </Button>
                    {send_menu_open ? (
                        <div
                            role='menu'
                            style={{
                                position: 'absolute',
                                right: 0,
                                bottom: '100%',
                                marginBottom: 4,
                                background: '#fff',
                                border: '1px solid #ecf0f1',
                                borderRadius: 4,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                minWidth: 220,
                                zIndex: 10,
                                overflow: 'hidden',
                            }}
                        >
                            <div
                                role='menuitem'
                                onClick={() => { set_send_mode('AI'); set_send_menu_open(false); }}
                                style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    fontSize: 13,
                                    background: send_mode === 'AI' ? '#f4f6f8' : '#fff',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#f4f6f8'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = send_mode === 'AI' ? '#f4f6f8' : '#fff'; }}
                            >
                                <span>
                                    <strong>Send to AI</strong>
                                    <span style={{ color: '#7f8c8d', marginLeft: 6, fontSize: 11 }}>(default)</span>
                                </span>
                                {send_mode === 'AI' ? <span style={{ color: '#3498db' }}>✓</span> : null}
                            </div>
                            <div
                                role='menuitem'
                                onClick={() => { set_send_mode('NOTE'); set_send_menu_open(false); }}
                                style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    fontSize: 13,
                                    background: send_mode === 'NOTE' ? '#f4f6f8' : '#fff',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    borderTop: '1px solid #ecf0f1',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#f4f6f8'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = send_mode === 'NOTE' ? '#f4f6f8' : '#fff'; }}
                            >
                                <span>Just add as note</span>
                                {send_mode === 'NOTE' ? <span style={{ color: '#3498db' }}>✓</span> : null}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
});
FindingChatPanel.displayName = 'FindingChatPanel';

/**
 * The AI's proposed response to this challenge, embedded into the same card.
 *
 * Findings UI revamp: the inline Approve / Dismiss buttons were removed — the
 * chat panel's `approve_proposed_solution` / `dismiss_proposed_solution`
 * tools now drive that part of the state machine. The "Convert to Actionable"
 * button stays as a direct, always-visible button because conversion needs a
 * structured form (owner + dueDate) the chat can't elicit cleanly.
 *
 * Visible states:
 *   PROPOSED / APPROVED → "Convert to Actionable →" button
 *   CONVERTED → links to the resulting Actionable(s)
 *   DISMISSED → shows the dismissal note
 */
const ProposedSolutionPanel = ({ store_id, solution, on_changed }) => {
    const [busy, set_busy] = useState(false);
    const [err, set_err] = useState(null);
    const [convert_open, set_convert_open] = useState(false);

    const status = solution.status;
    const status_color = SOLUTION_STATUS_COLOR[status] || '#7f8c8d';

    return (
        <div style={{
            marginTop: 16,
            padding: 12,
            background: '#f8fafb',
            border: `1px solid ${status_color}33`,
            borderLeft: `3px solid ${status_color}`,
            borderRadius: 4,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    💡 Proposed Solution
                </div>
                <Badge style={{ backgroundColor: status_color, color: '#fff' }}>{status}</Badge>
            </div>
            <div style={{ fontSize: 14, color: '#2c3e50', fontWeight: 500, marginBottom: 6 }}>
                {solution.description}
            </div>
            {solution.rationale ? (
                <div style={{ fontSize: 12, color: '#7f8c8d', fontStyle: 'italic', marginBottom: 8 }}>
                    Why: {solution.rationale}
                </div>
            ) : null}
            <div style={{ display: 'flex', gap: 8, fontSize: 11, marginBottom: 8, flexWrap: 'wrap' }}>
                <Badge style={{ backgroundColor: EFFORT_IMPACT_COLOR[solution.effort] || '#95a5a6', color: '#fff' }}>
                    effort: {solution.effort}
                </Badge>
                <Badge style={{ backgroundColor: EFFORT_IMPACT_COLOR[solution.expectedImpact] || '#95a5a6', color: '#fff' }}>
                    impact: {solution.expectedImpact}
                </Badge>
                <Badge color='secondary'>owner: {solution.suggestedOwnerSide}</Badge>
            </div>

            {status === 'DISMISSED' && solution.dismissalNote ? (
                <div style={{ fontSize: 12, color: '#7f8c8d', marginBottom: 8, padding: 6, background: '#ecf0f1', borderRadius: 3 }}>
                    <strong>Dismissed:</strong> {solution.dismissalNote}
                    {solution.dismissedBy ? <span style={{ marginLeft: 6 }}>— {solution.dismissedBy}</span> : null}
                </div>
            ) : null}

            {status === 'CONVERTED' && Array.isArray(solution.convertedActionables) && solution.convertedActionables.length > 0 ? (
                <div style={{ marginTop: 8, fontSize: 12 }}>
                    <div style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                        Resulting actionable{solution.convertedActionables.length > 1 ? 's' : ''}
                    </div>
                    {solution.convertedActionables.map((a) => (
                        <div key={a.id} style={{ padding: 4, marginTop: 2, background: '#fff', borderRadius: 3, border: '1px solid #ecf0f1' }}>
                            <Badge color='success' style={{ marginRight: 6 }}>{a.status}</Badge>
                            {a.description}
                            {a.owner ? <span style={{ color: '#7f8c8d', marginLeft: 6 }}>· {a.owner}</span> : null}
                            {a.dueDate ? <span style={{ color: '#7f8c8d', marginLeft: 6 }}>· due {fmt_date(a.dueDate)}</span> : null}
                        </div>
                    ))}
                </div>
            ) : null}

            {/* Convert is the only direct button left on the panel. Approve /
                Dismiss now run through the chat tools. Convert needs a
                structured form so it stays in-UI. Compromise from spec:
                shown for PROPOSED too (with a one-line hint), since the
                backend requires APPROVED to convert. From PROPOSED the rep
                should ask the chat to approve first ("approve this"). */}
            {err ? (
                <div style={{ color: '#c0392b', fontSize: 12, marginBottom: 6 }}>{err}</div>
            ) : null}
            {status === 'PROPOSED' || status === 'APPROVED' ? (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <Button
                        size='sm'
                        color='primary'
                        disabled={busy}
                        onClick={async () => {
                            // Auto-approve on Convert click when still PROPOSED.
                            // The backend convert endpoint requires APPROVED — so
                            // we silently approve first, then open the modal.
                            // The rep's click intent is unambiguous ("yes do
                            // something with this"); the intermediate approval
                            // step is just bookkeeping that doesn't need a
                            // separate UI action.
                            if (status === 'PROPOSED') {
                                try {
                                    set_busy(true);
                                    const approved = await cs_analysis_fetch({
                                        path: `/api/v1/clients/${encodeURIComponent(store_id)}/proposed-solutions/${solution.id}/approve`,
                                        method: 'POST',
                                    });
                                    on_changed(approved);
                                } catch (e) {
                                    set_err(e.message || String(e));
                                    set_busy(false);
                                    return;
                                } finally {
                                    set_busy(false);
                                }
                            }
                            set_convert_open(true);
                        }}
                    >
                        Convert to Task →
                    </Button>
                </div>
            ) : null}

            <ConvertSolutionModal
                open={convert_open}
                solution={solution}
                store_id={store_id}
                on_close={() => set_convert_open(false)}
                on_converted={(payload) => {
                    on_changed(payload.solution);
                    set_convert_open(false);
                }}
            />
        </div>
    );
};

// Quick-pick chips for the due-date field — covers the common cases so the
// rep doesn't have to open the native picker for "tomorrow" or "next week".
const DUE_DATE_QUICK_PICKS = [
    { label: 'Tomorrow', days: 1 },
    { label: '+3d', days: 3 },
    { label: '+1wk', days: 7 },
    { label: '+2wk', days: 14 },
    { label: '+1mo', days: 30 },
];
const date_n_days_hence_iso = (n) => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
};

/**
 * Modal for converting an APPROVED solution into a committed Actionable.
 * Captures owner + dueDate plus optional description override.
 *
 * Owner defaults to the client's assigned CS rep (csOwner.email from
 * /clients/:storeId/health). The owner-side field was removed — it was a
 * UI residue from the early prototype and isn't surfaced in the Actionables
 * list, so making the rep choose it was friction with no payoff.
 */
const ConvertSolutionModal = ({ open, solution, store_id, on_close, on_converted }) => {
    const [owner, set_owner] = useState('');
    const [due_date, set_due_date] = useState('');
    const [description, set_description] = useState('');
    const [busy, set_busy] = useState(false);
    const [err, set_err] = useState(null);
    const [default_owner_email, set_default_owner_email] = useState('');

    // Seed the form when the modal opens with this solution. Owner waits on
    // the /health fetch below — we don't want to flash an empty input then
    // suddenly populate it, so we leave it blank until the fetch settles and
    // let the user start typing if they're faster than the network.
    useEffect(() => {
        if (open && solution) {
            set_description(solution.description || '');
            set_owner('');
            set_due_date('');
            set_err(null);
            set_default_owner_email('');
        }
    }, [open, solution && solution.id]);

    // Fetch the assigned CS rep for this client so we can pre-fill Owner.
    // Cheap call (cached server-side) and avoids forcing the rep to know /
    // type their own email. If the client has no csOwner (unassigned), the
    // field stays empty and the user enters it manually.
    useEffect(() => {
        if (!open || !store_id) return;
        let cancelled = false;
        (async () => {
            try {
                const payload = await cs_analysis_fetch({
                    path: `/api/v1/clients/${encodeURIComponent(store_id)}/health`,
                });
                if (cancelled) return;
                const email = payload && payload.client && payload.client.csOwner && payload.client.csOwner.email;
                if (email) {
                    set_default_owner_email(email);
                    // Only seed if the user hasn't started typing yet
                    set_owner((curr) => (curr ? curr : email));
                }
            } catch (_e) {
                // Non-fatal — the rep can still type the owner manually
            }
        })();
        return () => { cancelled = true; };
    }, [open, store_id]);

    const submit = async () => {
        if (!owner.trim() || !due_date) {
            set_err('Owner and Due date are required.');
            return;
        }
        set_busy(true);
        set_err(null);
        try {
            const next = await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/proposed-solutions/${solution.id}/convert`,
                method: 'POST',
                body: {
                    owner: owner.trim(),
                    dueDate: new Date(due_date).toISOString(),
                    description: description.trim() && description.trim() !== solution.description ? description.trim() : undefined,
                },
            });
            on_converted(next);
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_busy(false);
        }
    };

    if (!solution) return null;
    return (
        <Modal isOpen={open} toggle={on_close} size='lg'>
            <ModalHeader toggle={on_close}>Convert to Task</ModalHeader>
            <ModalBody>
                <div style={{ fontSize: 12, color: '#7f8c8d', marginBottom: 12 }}>
                    Creates a committed task with the fields below. The proposed solution moves to CONVERTED.
                </div>
                <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Description</div>
                    <Input
                        type='textarea'
                        rows={6}
                        value={description}
                        onChange={(e) => set_description(e.target.value)}
                        style={{ fontSize: 13, lineHeight: 1.5 }}
                    />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                        Owner <span style={{ color: '#c0392b' }}>*</span>
                        {default_owner_email ? (
                            <span style={{ textTransform: 'none', letterSpacing: 0, color: '#95a5a6', marginLeft: 8, fontSize: 11 }}>
                                default: assigned CS rep
                            </span>
                        ) : null}
                    </div>
                    <Input
                        value={owner}
                        onChange={(e) => set_owner(e.target.value)}
                        placeholder='email or user id'
                    />
                </div>
                <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                        Due date <span style={{ color: '#c0392b' }}>*</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Input
                            type='date'
                            value={due_date}
                            onChange={(e) => set_due_date(e.target.value)}
                            style={{ width: 'auto', minWidth: 160 }}
                        />
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {DUE_DATE_QUICK_PICKS.map((q) => {
                                const iso = date_n_days_hence_iso(q.days);
                                const active = due_date === iso;
                                return (
                                    <Button
                                        key={q.label}
                                        size='sm'
                                        outline={!active}
                                        color={active ? 'primary' : 'secondary'}
                                        onClick={() => set_due_date(iso)}
                                        style={{ fontSize: 12, padding: '2px 8px' }}
                                    >
                                        {q.label}
                                    </Button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                {err ? <div style={{ color: '#c0392b', fontSize: 12, marginTop: 6 }}>{err}</div> : null}
            </ModalBody>
            <ModalFooter>
                <Button color='secondary' outline onClick={on_close}>Cancel</Button>
                <Button color='primary' disabled={busy || !owner.trim() || !due_date} onClick={submit}>
                    {busy ? 'Creating…' : 'Create Task'}
                </Button>
            </ModalFooter>
        </Modal>
    );
};


// Close options for "Mark Done". Terminal statuses only — the AI preselects
// one, the rep can override. Labels carry the meaning so no legend is needed.
const CLOSE_STATUS_OPTIONS = [
    { value: 'MITIGATED', label: 'Mitigated — worked around / partially addressed' },
    { value: 'RESOLVED', label: 'Resolved — fully fixed' },
    { value: 'DISMISSED', label: 'Dismissed — not a real issue / false positive' },
];

/**
 * "Mark Done" confirmation modal. Opens with an AI-suggested classification +
 * drafted closing comment (both editable), and on confirm PATCHes the finding
 * to the chosen terminal status with the comment as resolutionNote. The
 * parent fetches the suggestion before opening (passes it via `init`) and
 * shows a spinner via `loading` while that call is in flight.
 */
const CloseFindingModal = ({ open, loading, store_id, finding, init, on_close, on_done }) => {
    const [status, set_status] = useState('MITIGATED');
    const [comment, set_comment] = useState('');
    const [busy, set_busy] = useState(false);
    const [err, set_err] = useState(null);

    // Reseed from the AI suggestion each time the modal opens (or the
    // suggestion arrives). The rep's edits are local until they confirm.
    useEffect(() => {
        if (open) {
            set_status(init.suggestedStatus || 'MITIGATED');
            set_comment(init.draftComment || '');
            set_err(null);
        }
    }, [open, init.suggestedStatus, init.draftComment]);

    const submit = async () => {
        set_busy(true);
        set_err(null);
        try {
            const next = await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/findings/${finding.id}`,
                method: 'PATCH',
                body: { status, resolutionNote: comment.trim() || undefined },
            });
            on_done(next);
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_busy(false);
        }
    };

    return (
        <Modal isOpen={open} toggle={on_close}>
            <ModalHeader toggle={on_close}>Mark finding done</ModalHeader>
            <ModalBody>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 16, color: '#7f8c8d' }}>
                        <Spinner size='sm' /> <span style={{ marginLeft: 8 }}>AI drafting a closing comment…</span>
                    </div>
                ) : (
                    <div>
                        <div style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                            How it's being closed <span style={{ textTransform: 'none', letterSpacing: 0, color: '#95a5a6' }}>(AI-classified, editable)</span>
                        </div>
                        <Input
                            type='select'
                            bsSize='sm'
                            value={status}
                            onChange={(e) => set_status(e.target.value)}
                            style={{ marginBottom: 12, fontSize: 13 }}
                        >
                            {CLOSE_STATUS_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </Input>
                        <div style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                            Closing comment <span style={{ textTransform: 'none', letterSpacing: 0, color: '#95a5a6' }}>(AI-drafted, editable)</span>
                        </div>
                        <Input
                            type='textarea'
                            rows={4}
                            value={comment}
                            onChange={(e) => set_comment(e.target.value)}
                            placeholder='Closing comment…'
                            style={{ fontSize: 13, lineHeight: 1.5 }}
                        />
                        {err ? <div style={{ color: '#c0392b', fontSize: 12, marginTop: 6 }}>{err}</div> : null}
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color='secondary' outline onClick={on_close} disabled={busy}>Cancel</Button>
                <Button color='success' onClick={submit} disabled={busy || loading}>
                    {busy ? 'Saving…' : 'Confirm done'}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default Findings;
